import { z } from 'zod';

// ============= AI API Schemas =============

export const generateAIResponseSchema = z.object({
  prompt: z.string().min(1, 'Prompt cannot be empty').max(10000, 'Prompt is too long'),
  runtime: z.object({
    provider: z.enum(['openai', 'anthropic', 'azure', 'custom']),
    apiKey: z.string().trim().optional().default(''),
    model: z.string().trim().min(1, 'Model is required'),
    phase2Model: z.string().trim().min(1).optional(),
    baseUrl: z.string().trim().optional(),
    supportsStructuredOutputs: z.boolean().optional(),
  }),
  maxTokens: z.number().int().positive().optional(),
});

export type GenerateAIRequest = z.infer<typeof generateAIResponseSchema>;

export const runtimePoolItemSchema = generateAIResponseSchema.shape.runtime.extend({
  profileId: z.string().trim().min(1).max(120).optional(),
  profileName: z.string().trim().min(1).max(200).optional(),
});

export type RuntimePoolItem = z.infer<typeof runtimePoolItemSchema>;

export const tokenUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
});

export type TokenUsage = z.infer<typeof tokenUsageSchema>;

export const aiResponseSchema = z.object({
  id: z.string(),
  content: z.string(),
  model: z.string(),
  tokens: z.object({
    prompt: z.number(),
    completion: z.number(),
  }),
  createdAt: z.coerce.date(),
});

export type AIResponse = z.infer<typeof aiResponseSchema>;

// ============= Records/Data Schemas =============

export const recordSchema = z.object({
  id: z.string().optional(),
  title: z.string().min(1, 'Title is required').max(500),
  description: z.string().max(5000).optional(),
  content: z.string(),
  tags: z.array(z.string()).optional(),
  projectId: z.string().optional(),
  searchId: z.string().optional(),
  sourcePmid: z.string().optional(),
  journalKey: z.string().nullable().optional(),
  impactFactor: z.number().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional().nullable(),
  status: z.enum(['active', 'archived', 'deleted']).default('active'),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type Record = z.infer<typeof recordSchema>;

const writableRecordStatusSchema = z.enum(['active', 'archived']);

export const createRecordSchema = recordSchema
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({ status: writableRecordStatusSchema.default('active') });
export type CreateRecordRequest = z.infer<typeof createRecordSchema>;

export const updateRecordSchema = createRecordSchema.partial();
export type UpdateRecordRequest = z.infer<typeof updateRecordSchema>;

// ============= List Query Schemas =============

export const listQuerySchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  cursor: z.string().trim().min(1).max(500).optional(),
  search: z.string().optional(),
  textQuery: z.string().optional(),
  publicationTypes: z.string().optional(),
  minImpactFactor: z.number().nonnegative().optional(),
  status: z.enum(['active', 'archived', 'deleted']).optional(),
  projectId: z.string().optional(),
  searchId: z.string().optional(),
  searchIds: z.string().optional(),
});

export type ListQuery = z.infer<typeof listQuerySchema>;

export const listResponseSchema = z.object({
  items: z.array(recordSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
  nextCursor: z.string().optional(),
});

export type ListResponse<T = Record> = z.infer<typeof listResponseSchema> & { items: T[] };

export const recordPublicationTypeFacetsResponseSchema = z.object({
  items: z.array(z.string()),
});

export type RecordPublicationTypeFacetsResponse = z.infer<typeof recordPublicationTypeFacetsResponseSchema>;

// ============= Export Schemas =============

export const exportOptionsSchema = z.object({
  format: z.enum(['csv', 'json']).default('csv'),
  fields: z.array(z.string()).optional(),
  status: z.enum(['active', 'archived', 'deleted']).optional(),
});

export type ExportOptions = z.infer<typeof exportOptionsSchema>;

// ============= Project Schemas =============

export const projectTypeSchema = z.enum(['pubmed', 'pdf']);
export type ProjectType = z.infer<typeof projectTypeSchema>;

export const projectSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Project name is required').max(200),
  description: z.string().max(2000).optional().nullable(),
  type: projectTypeSchema.default('pubmed'),
  status: z.enum(['active', 'archived']).default('active'),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Project = z.infer<typeof projectSchema>;

export const createProjectSchema = z.object({
  name: z.string().min(1, 'Project name is required').max(200),
  description: z.string().max(2000).optional().nullable(),
  type: projectTypeSchema.default('pubmed'),
});
export type CreateProjectRequest = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = createProjectSchema.omit({ type: true }).partial();
export type UpdateProjectRequest = z.infer<typeof updateProjectSchema>;

// ============= Project Search Schemas =============

export const pubmedArticlePayloadSchema = z.object({
  pmid: z.string().min(1).max(64),
  title: z.string(),
  abstractText: z.string(),
  journalTitle: z.string(),
  publicationDateText: z.string(),
  publicationTypes: z.array(z.string()),
  authors: z.array(z.string()),
  doi: z.string().nullable(),
  volume: z.string().nullable(),
  issue: z.string().nullable(),
  pages: z.string().nullable(),
  impactFactor: z.number().nullable().optional().default(null),
});

export type PubmedArticlePayload = z.infer<typeof pubmedArticlePayloadSchema>;

export const projectSearchKindSchema = z.enum(['search', 'pre_analysis', 'pdf_ocr']);
export type ProjectSearchKind = z.infer<typeof projectSearchKindSchema>;

export const createProjectSearchSchema = z.object({
  query: z.string().min(1).max(100000),
  searchId: z.string().min(1).max(100).optional(),
  name: z.string().trim().min(1).max(200).optional(),
  kind: projectSearchKindSchema.default('search'),
  sourceSearchIds: z.array(z.string().min(1).max(100)).max(500).optional(),
  items: z.array(pubmedArticlePayloadSchema).max(3000),
});

export type CreateProjectSearchRequest = z.infer<typeof createProjectSearchSchema>;

export const projectSearchRunSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  query: z.string(),
  name: z.string().nullable().optional(),
  kind: projectSearchKindSchema.default('search'),
  sourceSearchIds: z.array(z.string()).optional(),
  totalCount: z.number().int().nonnegative(),
  savedCount: z.number().int().nonnegative(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type ProjectSearchRun = z.infer<typeof projectSearchRunSchema>;

export const createProjectSearchResponseSchema = z.object({
  searchId: z.string(),
  projectId: z.string(),
  totalRequested: z.number().int().nonnegative(),
  totalSaved: z.number().int().nonnegative(),
  run: projectSearchRunSchema,
});

export type CreateProjectSearchResponse = z.infer<typeof createProjectSearchResponseSchema>;

export const executeProjectPubmedSearchRequestSchema = z.object({
  query: z.string().min(1).max(100000),
  searchId: z.string().min(1).max(100).optional(),
  name: z.string().trim().min(1).max(200).optional(),
  retmax: z.number().int().min(1).max(3000).optional().default(3000),
  batchSize: z.number().int().min(1).max(200).optional().default(50),
  delayMs: z.number().int().min(0).max(2000).optional().default(350),
});

export type ExecuteProjectPubmedSearchRequest = z.infer<typeof executeProjectPubmedSearchRequestSchema>;

export const executeProjectPubmedSearchProgressEventSchema = z.object({
  type: z.literal('progress'),
  completedBatches: z.number().int().nonnegative(),
  totalBatches: z.number().int().nonnegative(),
  loadedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  stage: z.enum(['count', 'fetch', 'save']),
});

export const executeProjectPubmedSearchBatchItemsEventSchema = z.object({
  type: z.literal('batch_items'),
  items: z.array(pubmedArticlePayloadSchema),
});

export const executeProjectPubmedSearchSavedEventSchema = z.object({
  type: z.literal('saved'),
  searchId: z.string(),
  projectId: z.string(),
  totalRequested: z.number().int().nonnegative(),
  totalSaved: z.number().int().nonnegative(),
  run: projectSearchRunSchema,
});

export const executeProjectPubmedSearchDoneEventSchema = z.object({
  type: z.literal('done'),
});

export const executeProjectPubmedSearchErrorEventSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
  code: z.string().optional(),
});

export const executeProjectPubmedSearchSseEventSchema = z.discriminatedUnion('type', [
  executeProjectPubmedSearchProgressEventSchema,
  executeProjectPubmedSearchBatchItemsEventSchema,
  executeProjectPubmedSearchSavedEventSchema,
  executeProjectPubmedSearchDoneEventSchema,
  executeProjectPubmedSearchErrorEventSchema,
]);

export type ExecuteProjectPubmedSearchSseEvent = z.infer<typeof executeProjectPubmedSearchSseEventSchema>;

export const projectSearchDeletePreviewSchema = z.object({
  searchId: z.string(),
  projectId: z.string(),
  query: z.string(),
  name: z.string().nullable().optional(),
  kind: projectSearchKindSchema.default('search'),
  totalCount: z.number().int().nonnegative(),
  savedCount: z.number().int().nonnegative(),
  recordCount: z.number().int().nonnegative(),
  approxBytes: z.number().int().nonnegative(),
});

export type ProjectSearchDeletePreview = z.infer<typeof projectSearchDeletePreviewSchema>;

// ============= PDF OCR Batch Schemas =============

export const pdfOcrModelSchema = z.enum(['PaddleOCR-VL-1.5', 'DeepSeek-OCR']);
export type PdfOcrModel = z.infer<typeof pdfOcrModelSchema>;

export const pdfOcrTaskStatusSchema = z.enum(['pending', 'processing', 'success', 'failed', 'deduped']);
export type PdfOcrTaskStatus = z.infer<typeof pdfOcrTaskStatusSchema>;

export const pdfOcrTaskPhaseSchema = z.enum([
  'queued',
  'rendering',
  'ocr',
  'saving',
  'completed',
  'deduped',
  'failed',
]);
export type PdfOcrTaskPhase = z.infer<typeof pdfOcrTaskPhaseSchema>;

export const pdfOcrTaskActiveKeySchema = z.object({
  keySlot: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  phase: z.enum(['rendering', 'ocr']),
});
export type PdfOcrTaskActiveKey = z.infer<typeof pdfOcrTaskActiveKeySchema>;

export const pdfOcrTaskProgressSchema = z.object({
  phase: pdfOcrTaskPhaseSchema,
  totalPages: z.number().int().positive().optional(),
  currentPage: z.number().int().positive().optional(),
  renderedPages: z.number().int().nonnegative().optional(),
  ocrPages: z.number().int().nonnegative().optional(),
  keySlot: z.number().int().nonnegative().optional(),
  keyCount: z.number().int().positive().optional(),
  activeKeys: z.array(pdfOcrTaskActiveKeySchema).max(64).optional(),
  markdownChars: z.number().int().nonnegative().optional(),
  message: z.string().trim().min(1).max(500).optional(),
  updatedAt: z.coerce.date().optional(),
});
export type PdfOcrTaskProgress = z.infer<typeof pdfOcrTaskProgressSchema>;

export const pdfOcrTaskMetadataSchema = z.object({
  title: z.string().trim().min(1).max(2000).optional(),
  doi: z.string().trim().min(1).max(512).optional(),
  doiCandidates: z.array(z.string().trim().min(1).max(512)).max(50).optional(),
  author: z.string().trim().min(1).max(1000).optional(),
  subject: z.string().trim().min(1).max(2000).optional(),
  keywords: z.string().trim().min(1).max(2000).optional(),
  creator: z.string().trim().min(1).max(1000).optional(),
  producer: z.string().trim().min(1).max(1000).optional(),
  creationDate: z.string().trim().min(1).max(200).optional(),
  modificationDate: z.string().trim().min(1).max(200).optional(),
  language: z.string().trim().min(1).max(120).optional(),
  encrypted: z.string().trim().min(1).max(120).optional(),
  pageCount: z.number().int().positive().optional(),
  fileSizeBytes: z.number().int().nonnegative().optional(),
  rawInfo: z.record(z.string(), z.string()).optional(),
});
export type PdfOcrTaskMetadata = z.infer<typeof pdfOcrTaskMetadataSchema>;

export const initPdfOcrBatchRequestSchema = z.object({
  totalFiles: z.number().int().min(1).max(200),
});

export type InitPdfOcrBatchRequest = z.infer<typeof initPdfOcrBatchRequestSchema>;

export const renderedPdfPageSchema = z.object({
  pageNumber: z.number().int().positive(),
  imageDataUrl: z.string().trim().min(40).max(8_000_000).refine(value => value.startsWith('data:image/jpeg;base64,'), {
    message: 'Only JPEG data URLs are supported',
  }),
});
export type RenderedPdfPage = z.infer<typeof renderedPdfPageSchema>;

export const runPdfOcrTaskRequestSchema = z.object({
  runId: z.string().trim().min(1).max(120),
  fileName: z.string().trim().min(1).max(500),
  fileHash: z
    .string()
    .trim()
    .length(64)
    .regex(/^[a-f0-9]{64}$/i, 'fileHash must be a 64-char hex sha256'),
  pages: z.array(renderedPdfPageSchema).min(1).max(20),
});

export type RunPdfOcrTaskRequest = z.infer<typeof runPdfOcrTaskRequestSchema>;

export const pdfOcrTaskSchema = z.object({
  id: z.string().trim().min(1).max(120),
  runId: z.string().trim().min(1).max(120),
  fileName: z.string().trim().min(1).max(500),
  fileHash: z.string().trim().length(64),
  status: pdfOcrTaskStatusSchema,
  recordId: z.string().trim().min(1).max(120).optional(),
  dedupHit: z.boolean().default(false),
  message: z.string().trim().min(1).max(2000).optional(),
  progress: pdfOcrTaskProgressSchema.optional(),
  pdfMetadata: pdfOcrTaskMetadataSchema.optional(),
  previewMarkdown: z.string().max(200000).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type PdfOcrTask = z.infer<typeof pdfOcrTaskSchema>;

export const pdfOcrBatchSummarySchema = z.object({
  runId: z.string().trim().min(1).max(120),
  projectId: z.string().trim().min(1).max(120),
  model: pdfOcrModelSchema,
  totalCount: z.number().int().nonnegative(),
  processedCount: z.number().int().nonnegative(),
  successCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  dedupedCount: z.number().int().nonnegative(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type PdfOcrBatchSummary = z.infer<typeof pdfOcrBatchSummarySchema>;

export const pdfOcrBatchDetailSchema = pdfOcrBatchSummarySchema.extend({
  tasks: z.array(pdfOcrTaskSchema),
});

export type PdfOcrBatchDetail = z.infer<typeof pdfOcrBatchDetailSchema>;

export const initPdfOcrBatchResponseSchema = z.object({
  batch: pdfOcrBatchDetailSchema,
});

export type InitPdfOcrBatchResponse = z.infer<typeof initPdfOcrBatchResponseSchema>;

export const pdfOcrTaskResponseSchema = z.object({
  batch: pdfOcrBatchDetailSchema,
  task: pdfOcrTaskSchema,
});

export type PdfOcrTaskResponse = z.infer<typeof pdfOcrTaskResponseSchema>;

export const pdfOcrTaskDetailResponseSchema = z.object({
  task: pdfOcrTaskSchema,
});

export type PdfOcrTaskDetailResponse = z.infer<typeof pdfOcrTaskDetailResponseSchema>;

// ============= AI Structured Fields v2 =============

export const decisionChainSchema = z.object({
  judgementPrompt: z.string().trim().min(1).max(3000),
  yesRule: z.string().trim().min(1).max(3000),
  noRule: z.string().trim().min(1).max(3000),
  reasonRule: z.string().trim().min(1).max(3000),
});

export type DecisionChain = z.infer<typeof decisionChainSchema>;

export const evidenceKeywordsSchema = z.object({
  positive: z.array(z.string()).default([]),
  negative: z.array(z.string()).default([]),
});

export type EvidenceKeywords = z.infer<typeof evidenceKeywordsSchema>;

export const fieldDecisionCardClassSchema = z.enum(['screening_boolean', 'extraction_value']);
export type FieldDecisionCardClass = z.infer<typeof fieldDecisionCardClassSchema>;

function toTrimmedString(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

export function inferFieldDecisionCardClass(input: { key?: unknown; label?: unknown }): FieldDecisionCardClass {
  const label = toTrimmedString(input.label);
  if (label.startsWith('是否')) {
    return 'screening_boolean';
  }

  const key = toTrimmedString(input.key).toLowerCase();
  const isBooleanPrefix =
    (key.startsWith('is') && key !== 'issue' && key.length > 2) ||
    (key.startsWith('has') && key.length > 3);
  if (isBooleanPrefix) {
    return 'screening_boolean';
  }

  return 'extraction_value';
}

export function resolveFieldDecisionCardClass(input: {
  fieldClass?: unknown;
  key?: unknown;
  label?: unknown;
}): FieldDecisionCardClass {
  const inferred = inferFieldDecisionCardClass(input);
  if (inferred === 'screening_boolean') {
    return inferred;
  }

  const parsed = fieldDecisionCardClassSchema.safeParse(input.fieldClass);
  if (parsed.success) {
    return parsed.data;
  }
  return inferred;
}

export const fieldDecisionCardSchema = z.object({
  key: z.string().trim().min(1).max(120),
  label: z.string().trim().min(1).max(200),
  fieldClass: fieldDecisionCardClassSchema.optional(),
  applicability: z.string().trim().min(1).max(2000),
  decisionChain: decisionChainSchema,
  outputTemplate: z.string().trim().min(1).max(1000),
  evidenceKeywords: evidenceKeywordsSchema,
});

export type FieldDecisionCard = z.infer<typeof fieldDecisionCardSchema>;

export const extractFieldTargetSchema = z.object({
  fieldKey: z.string().trim().min(1).max(120),
  targetDecision: z.enum(['yes', 'no']),
});

export type ExtractFieldTarget = z.infer<typeof extractFieldTargetSchema>;

export const extractBatchFieldSchema = fieldDecisionCardSchema.extend({
  targetDecision: z.enum(['yes', 'no']),
});

export type ExtractBatchField = z.infer<typeof extractBatchFieldSchema>;

export const structuredFieldsScopeSchema = z.enum(['builder', 'results_header_builder']);
export type StructuredFieldsScope = z.infer<typeof structuredFieldsScopeSchema>;

export const structuredFieldsV2RequestSchema = generateAIResponseSchema.extend({
  scope: structuredFieldsScopeSchema.default('builder'),
  criteriaText: z.string().trim().min(1).max(20000),
  projectContextRuns: z.array(projectSearchRunSchema).max(500).default([]),
});

export type StructuredFieldsV2Request = z.infer<typeof structuredFieldsV2RequestSchema>;

export const pubmedHeaderKeySchema = z.enum([
  'pmid',
  'title',
  'reference',
  'authors',
  'journalTitle',
  'publicationDateText',
  'publicationTypes',
  'doi',
  'volume',
  'issue',
  'pages',
  'impactFactor',
  'fileHash',
  'fileName',
  'author',
  'subject',
  'keywords',
  'creator',
  'producer',
  'creationDate',
  'modificationDate',
  'language',
  'encrypted',
  'pageCount',
  'fileSizeBytes',
]);

export type PubmedHeaderKey = z.infer<typeof pubmedHeaderKeySchema>;

export const triageTemplateScopeSchema = z.enum(['builder', 'results_header_builder', 'extractor']);
export type TriageTemplateScope = z.infer<typeof triageTemplateScopeSchema>;

export const extractorTargetDecisionSchema = z.enum(['yes', 'no']);
export type ExtractorTargetDecision = z.infer<typeof extractorTargetDecisionSchema>;

export const extractorTargetsSchema = z.record(
  z.string().trim().min(1).max(120),
  extractorTargetDecisionSchema
);

export const triageFieldTemplateSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string().nullable().optional(),
  scope: triageTemplateScopeSchema.default('builder'),
  criteriaText: z.string(),
  reasoningText: z.string(),
  fields: z.array(fieldDecisionCardSchema),
  pubmedHeaderKeys: z.array(pubmedHeaderKeySchema).default([]),
  headerCardOrder: z.array(z.string().trim().min(1).max(120)).default([]),
  extractorTargets: extractorTargetsSchema.default({}),
  provider: z.string(),
  model: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type TriageFieldTemplate = z.infer<typeof triageFieldTemplateSchema>;

export const createTriageFieldTemplateSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  scope: triageTemplateScopeSchema.optional(),
  criteriaText: z.string().trim().min(1).max(20000),
  reasoningText: z.string().trim().min(1).max(60000),
  fields: z.array(fieldDecisionCardSchema).min(1).max(100),
  pubmedHeaderKeys: z.array(pubmedHeaderKeySchema).optional(),
  headerCardOrder: z.array(z.string().trim().min(1).max(120)).max(300).optional(),
  extractorTargets: extractorTargetsSchema.optional(),
  provider: z.string().trim().min(1).max(100),
  model: z.string().trim().min(1).max(200),
});

export type CreateTriageFieldTemplateRequest = z.infer<typeof createTriageFieldTemplateSchema>;

export const updateTriageFieldTemplateSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    scope: triageTemplateScopeSchema.optional(),
    criteriaText: z.string().trim().min(1).max(20000).optional(),
    reasoningText: z.string().trim().min(1).max(60000).optional(),
    fields: z.array(fieldDecisionCardSchema).min(1).max(100).optional(),
    pubmedHeaderKeys: z.array(pubmedHeaderKeySchema).optional(),
    headerCardOrder: z.array(z.string().trim().min(1).max(120)).max(300).optional(),
    extractorTargets: extractorTargetsSchema.optional(),
    provider: z.string().trim().min(1).max(100).optional(),
    model: z.string().trim().min(1).max(200).optional(),
  })
  .refine((input: { [key: string]: unknown }) => Object.keys(input).length > 0, {
    message: 'At least one field must be provided',
  });

export type UpdateTriageFieldTemplateRequest = z.infer<typeof updateTriageFieldTemplateSchema>;

export const listUserTriageTemplatesQuerySchema = z.object({
  projectId: z.string().trim().min(1).max(120).optional(),
  scope: triageTemplateScopeSchema.optional(),
  keyword: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(100),
  offset: z.coerce.number().int().min(0).max(5000).default(0),
});

export type ListUserTriageTemplatesQuery = z.infer<typeof listUserTriageTemplatesQuerySchema>;

export const userTriageTemplateListItemSchema = z.object({
  id: z.string().trim().min(1).max(120),
  projectId: z.string().trim().min(1).max(120),
  name: z.string().nullable(),
  scope: triageTemplateScopeSchema.default('builder'),
  provider: z.string().trim().min(1).max(100),
  model: z.string().trim().min(1).max(200),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  projectName: z.string().trim().min(1).max(200),
  projectType: projectTypeSchema,
  projectStatus: z.enum(['active', 'archived']),
  firstFieldKey: z.string().trim().min(1).max(120).nullable().optional(),
  firstFieldLabel: z.string().trim().min(1).max(200).nullable().optional(),
});

export type UserTriageTemplateListItem = z.infer<typeof userTriageTemplateListItemSchema>;

export const userTriageTemplateDetailSchema = triageFieldTemplateSchema;

export type UserTriageTemplateDetail = z.infer<typeof userTriageTemplateDetailSchema>;

export const listUserTriageTemplatesResponseSchema = z.object({
  items: z.array(userTriageTemplateListItemSchema),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

export type ListUserTriageTemplatesResponse = z.infer<typeof listUserTriageTemplatesResponseSchema>;

// ============= PubMed Text Query Templates =============

export const pubmedTextQueryTemplateSchema = z.object({
  id: z.string().trim().min(1).max(120),
  name: z.string().trim().min(1).max(200),
  query: z.string().trim().min(1).max(20000),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type PubmedTextQueryTemplate = z.infer<typeof pubmedTextQueryTemplateSchema>;

export const createPubmedTextQueryTemplateSchema = z.object({
  name: z.string().trim().min(1).max(200),
  query: z.string().trim().min(1).max(20000),
});

export type CreatePubmedTextQueryTemplateRequest = z.infer<typeof createPubmedTextQueryTemplateSchema>;

export const updatePubmedTextQueryTemplateSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    query: z.string().trim().min(1).max(20000).optional(),
  })
  .refine((input: { [key: string]: unknown }) => Object.keys(input).length > 0, {
    message: 'At least one field must be provided',
  });

export type UpdatePubmedTextQueryTemplateRequest = z.infer<typeof updatePubmedTextQueryTemplateSchema>;

// ============= AI Field Extraction Batch =============

export const extractFieldBatchItemSchema = z.object({
  recordId: z.string().trim().min(1).max(120),
  pmid: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(500),
  abstractText: z.string().trim().min(1).max(40000),
});

export type ExtractFieldBatchItem = z.infer<typeof extractFieldBatchItemSchema>;

export const fieldExtractionDecisionSchema = z.object({
  fieldKey: z.string().trim().min(1).max(120),
  fieldLabel: z.string().trim().min(1).max(200),
  decision: z.enum(['yes', 'no', 'unknown']),
  rawDecision: z.string().trim().max(120).optional(),
  reason: z.string().trim().min(1).max(4000),
  evidenceQuote: z.string().trim().min(1).max(4000),
});

export type FieldExtractionDecision = z.infer<typeof fieldExtractionDecisionSchema>;

export const abstractExtractionResultSchema = z.object({
  recordId: z.string().trim().min(1).max(120),
  pmid: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(500),
  summaryDecision: z.enum(['include', 'exclude']),
  needsManualReview: z.boolean().default(false),
  matchedYesCount: z.number().int().nonnegative(),
  totalFields: z.number().int().positive(),
  fieldDecisions: z.array(fieldExtractionDecisionSchema),
  rawModelJson: z.unknown(),
});

export type AbstractExtractionResult = z.infer<typeof abstractExtractionResultSchema>;

export const fieldReviewSchema = z.object({
  fieldKey: z.string().trim().min(1).max(120),
  actualDecision: z.enum(['yes', 'no']),
  overrideNote: z.string().trim().max(2000).optional(),
});

export type FieldReview = z.infer<typeof fieldReviewSchema>;

export const recordReviewSchema = z.object({
  recordId: z.string().trim().min(1).max(120),
  fieldReviews: z.array(fieldReviewSchema).min(1).max(100),
  reviewedAt: z.coerce.date(),
});

export type RecordReview = z.infer<typeof recordReviewSchema>;

export const batchReviewsSchema = z.record(z.string().trim().min(1).max(120), recordReviewSchema);

export type BatchReviews = z.infer<typeof batchReviewsSchema>;

export const saveRecordReviewRequestSchema = z.object({
  fieldReviews: z.array(fieldReviewSchema).min(1).max(100),
});

export type SaveRecordReviewRequest = z.infer<typeof saveRecordReviewRequestSchema>;

export const saveRecordReviewResponseSchema = z.object({
  review: recordReviewSchema,
});

export type SaveRecordReviewResponse = z.infer<typeof saveRecordReviewResponseSchema>;

export const extractFieldBatchRequestSchema = z.object({
  projectId: z.string().trim().min(1).max(120),
  templateId: z.string().trim().min(1).max(120).optional(),
  searchRunIds: z.array(z.string().trim().min(1).max(120)).min(1).max(500),
  fields: z.array(extractBatchFieldSchema).min(1).max(100),
  items: z.array(extractFieldBatchItemSchema).min(1).max(3000),
  runtime: generateAIResponseSchema.shape.runtime,
  runtimePool: z.array(runtimePoolItemSchema).max(50).optional(),
  maxTokens: z.number().int().positive().optional(),
  concurrency: z.number().int().min(1).max(6).default(3),
});

export type ExtractFieldBatchRequest = z.infer<typeof extractFieldBatchRequestSchema>;

export const extractFieldBatchRunSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  templateId: z.string().nullable().optional(),
  name: z.string().trim().min(1).max(120).nullable().optional(),
  searchRunIds: z.array(z.string()),
  totalCount: z.number().int().nonnegative(),
  successCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  provider: z.string(),
  model: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type ExtractFieldBatchRun = z.infer<typeof extractFieldBatchRunSchema>;

export const extractFieldBatchDetailSchema = extractFieldBatchRunSchema.extend({
  request: z.record(z.string(), z.unknown()),
  results: z.array(abstractExtractionResultSchema),
  reviews: batchReviewsSchema.default({}),
});

export type ExtractFieldBatchDetail = z.infer<typeof extractFieldBatchDetailSchema>;

export const updateTriageExtractionBatchSchema = z
  .object({
    name: z.string().trim().min(1).max(120).nullable().optional(),
  })
  .refine((input: { [key: string]: unknown }) => Object.keys(input).length > 0, {
    message: 'At least one field must be provided',
  });

export type UpdateTriageExtractionBatchRequest = z.infer<typeof updateTriageExtractionBatchSchema>;

export const createTriageExtractionSnapshotSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  templateId: z.string().trim().min(1).max(120).optional(),
  searchRunIds: z.array(z.string().trim().min(1).max(120)).min(1).max(500),
  fields: z.array(extractBatchFieldSchema).min(1).max(100),
  items: z.array(extractFieldBatchItemSchema).min(1).max(3000),
  results: z.array(abstractExtractionResultSchema).min(1).max(3000),
  totalCount: z.number().int().min(1).max(3000).optional(),
  successCount: z.number().int().min(0).max(3000).optional(),
  failedCount: z.number().int().min(0).max(3000).optional(),
  reviews: batchReviewsSchema.optional(),
  usage: tokenUsageSchema.optional(),
  provider: z.string().trim().min(1).max(100),
  model: z.string().trim().min(1).max(200),
});

export type CreateTriageExtractionSnapshotRequest = z.infer<typeof createTriageExtractionSnapshotSchema>;

export const createTriageExtractionSnapshotResponseSchema = z.object({
  batch: extractFieldBatchDetailSchema,
});

export type CreateTriageExtractionSnapshotResponse = z.infer<typeof createTriageExtractionSnapshotResponseSchema>;

// ============= Final Analysis Batch (Tab4) =============

export const headerTemplateSnapshotSchema = z.object({
  templateId: z.string().trim().min(1).max(120),
  pubmedHeaderKeys: z.array(pubmedHeaderKeySchema).default([]),
  aiFields: z.array(fieldDecisionCardSchema).min(1).max(100),
  headerCardOrder: z.array(z.string().trim().min(1).max(120)).min(1).max(300),
});

export type HeaderTemplateSnapshot = z.infer<typeof headerTemplateSnapshotSchema>;

export const analysisBatchItemMetadataSchema = z.object({
  journalTitle: z.string().optional().default(''),
  publicationDateText: z.string().optional().default(''),
  publicationTypes: z.array(z.string()).optional().default([]),
  authors: z.array(z.string()).optional().default([]),
  doi: z.string().nullable().optional().default(null),
  volume: z.string().nullable().optional().default(null),
  issue: z.string().nullable().optional().default(null),
  pages: z.string().nullable().optional().default(null),
  impactFactor: z.number().nullable().optional().default(null),
  fileHash: z.string().optional().default(''),
  fileName: z.string().optional().default(''),
  author: z.string().optional().default(''),
  subject: z.string().optional().default(''),
  keywords: z.string().optional().default(''),
  creator: z.string().optional().default(''),
  producer: z.string().optional().default(''),
  creationDate: z.string().optional().default(''),
  modificationDate: z.string().optional().default(''),
  language: z.string().optional().default(''),
  encrypted: z.string().optional().default(''),
  pageCount: z.number().int().positive().nullable().optional().default(null),
  fileSizeBytes: z.number().int().nonnegative().nullable().optional().default(null),
});

export type AnalysisBatchItemMetadata = z.infer<typeof analysisBatchItemMetadataSchema>;

export const analysisBatchItemSchema = z.object({
  recordId: z.string().trim().min(1).max(120),
  pmid: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(500),
  abstractText: z.string().trim().min(1).max(40000),
  metadata: analysisBatchItemMetadataSchema.optional(),
});

export type AnalysisBatchItem = z.infer<typeof analysisBatchItemSchema>;

export const analysisAiFieldResultSchema = z.object({
  fieldKey: z.string().trim().min(1).max(120),
  fieldLabel: z.string().trim().min(1).max(200),
  value: z.string().trim().max(8000),
  reason: z.string().trim().max(4000),
  evidenceQuote: z.string().trim().max(4000),
});

export type AnalysisAiFieldResult = z.infer<typeof analysisAiFieldResultSchema>;

export const analysisRecordResultSchema = z.object({
  recordId: z.string().trim().min(1).max(120),
  pmid: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(500),
  pubmedValues: z.record(z.string().trim().min(1).max(120), z.string()),
  aiFieldResults: z.array(analysisAiFieldResultSchema),
  rawModelJson: z.unknown(),
});

export type AnalysisRecordResult = z.infer<typeof analysisRecordResultSchema>;

export const analysisErrorItemSchema = z.object({
  recordId: z.string().trim().min(1).max(120),
  pmid: z.string().trim().min(1).max(120),
  title: z.string().trim().min(1).max(500),
  message: z.string().trim().min(1).max(4000),
});

export type AnalysisErrorItem = z.infer<typeof analysisErrorItemSchema>;

export const analysisBatchRequestSchema = z.object({
  projectId: z.string().trim().min(1).max(120),
  includeSearchRunIds: z.array(z.string().trim().min(1).max(120)).min(1).max(500),
  headerTemplateId: z.string().trim().min(1).max(120),
  headerTemplateSnapshot: headerTemplateSnapshotSchema,
  items: z.array(analysisBatchItemSchema).min(1).max(3000),
  runtime: generateAIResponseSchema.shape.runtime,
  runtimePool: z.array(runtimePoolItemSchema).max(50).optional(),
  maxTokens: z.number().int().positive().optional(),
  concurrency: z.number().int().min(1).max(6).optional(),
});

export type AnalysisBatchRequest = z.infer<typeof analysisBatchRequestSchema>;

export const analysisBatchRunSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  templateId: z.string().nullable().optional(),
  includeSearchRunIds: z.array(z.string()),
  totalCount: z.number().int().nonnegative(),
  successCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  provider: z.string(),
  model: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type AnalysisBatchRun = z.infer<typeof analysisBatchRunSchema>;

export const analysisBatchDetailSchema = analysisBatchRunSchema.extend({
  request: z.record(z.string(), z.unknown()),
  results: z.array(analysisRecordResultSchema),
  errors: z.array(analysisErrorItemSchema),
});

export type AnalysisBatchDetail = z.infer<typeof analysisBatchDetailSchema>;

export const createTriageAnalysisSnapshotSchema = z.object({
  includeSearchRunIds: z.array(z.string().trim().min(1).max(120)).min(1).max(500),
  headerTemplateId: z.string().trim().min(1).max(120),
  headerTemplateSnapshot: headerTemplateSnapshotSchema,
  items: z.array(analysisBatchItemSchema).min(1).max(3000),
  results: z.array(analysisRecordResultSchema).max(3000).default([]),
  errors: z.array(analysisErrorItemSchema).max(3000).default([]),
  totalCount: z.number().int().min(1).max(3000).optional(),
  successCount: z.number().int().min(0).max(3000).optional(),
  failedCount: z.number().int().min(0).max(3000).optional(),
  usage: tokenUsageSchema.optional(),
  provider: z.string().trim().min(1).max(100),
  model: z.string().trim().min(1).max(200),
});

export type CreateTriageAnalysisSnapshotRequest = z.infer<typeof createTriageAnalysisSnapshotSchema>;

export const createTriageAnalysisSnapshotResponseSchema = z.object({
  batch: analysisBatchDetailSchema,
});

export type CreateTriageAnalysisSnapshotResponse = z.infer<typeof createTriageAnalysisSnapshotResponseSchema>;

export const appendTriageAnalysisBatchSchema = z.object({
  includeSearchRunIds: z.array(z.string().trim().min(1).max(120)).min(1).max(500).optional(),
  items: z.array(analysisBatchItemSchema).min(1).max(3000),
  results: z.array(analysisRecordResultSchema).max(3000).default([]),
  errors: z.array(analysisErrorItemSchema).max(3000).default([]),
  totalCount: z.number().int().min(1).max(3000).optional(),
  successCount: z.number().int().min(0).max(3000).optional(),
  failedCount: z.number().int().min(0).max(3000).optional(),
  usage: tokenUsageSchema.optional(),
  provider: z.string().trim().min(1).max(100).optional(),
  model: z.string().trim().min(1).max(200).optional(),
});

export type AppendTriageAnalysisBatchRequest = z.infer<typeof appendTriageAnalysisBatchSchema>;

export const appendTriageAnalysisBatchResponseSchema = z.object({
  batch: analysisBatchDetailSchema,
});

export type AppendTriageAnalysisBatchResponse = z.infer<typeof appendTriageAnalysisBatchResponseSchema>;

// ============= Admin Dashboard Schemas =============

export const adminDashboardWindowSchema = z.enum(['7d', '30d', '90d']).default('30d');
export type AdminDashboardWindow = z.infer<typeof adminDashboardWindowSchema>;

export const adminDashboardOverviewSchema = z.object({
  window: adminDashboardWindowSchema,
  users: z.object({
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    suspended: z.number().int().nonnegative(),
    newInWindow: z.number().int().nonnegative(),
  }),
  invitations: z.object({
    total: z.number().int().nonnegative(),
    used: z.number().int().nonnegative(),
    unused: z.number().int().nonnegative(),
  }),
  usage: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    totalTokens: z.number().int().nonnegative(),
    requests: z.number().int().nonnegative(),
  }),
  data: z.object({
    projects: z.number().int().nonnegative(),
    records: z.number().int().nonnegative(),
  }),
  generatedAt: z.coerce.date(),
});

export type AdminDashboardOverview = z.infer<typeof adminDashboardOverviewSchema>;

export const adminDashboardUserSchema = z.object({
  id: z.string().trim().min(1).max(120),
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email(),
  role: z.string().trim().min(1).max(50),
  isSuspended: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  suspendedAt: z.coerce.date().nullable(),
  suspendedReason: z.string().nullable(),
  suspendedByUserId: z.string().nullable(),
  lastSignInAt: z.coerce.date().nullable(),
});

export type AdminDashboardUser = z.infer<typeof adminDashboardUserSchema>;

export const adminDashboardUsersResponseSchema = z.object({
  items: z.array(adminDashboardUserSchema),
  pagination: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  }),
});

export type AdminDashboardUsersResponse = z.infer<typeof adminDashboardUsersResponseSchema>;

export const adminDashboardUsageBySourceSchema = z.object({
  source: z.string().trim().min(1).max(120),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  requests: z.number().int().nonnegative(),
});

export const adminDashboardUsageSchema = z.object({
  userId: z.string().trim().min(1).max(120),
  window: adminDashboardWindowSchema,
  totals: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    totalTokens: z.number().int().nonnegative(),
    requests: z.number().int().nonnegative(),
  }),
  bySource: z.array(adminDashboardUsageBySourceSchema),
  storage: z.object({
    totalBytes: z.number().int().nonnegative(),
    byTable: z.array(
      z.object({
        table: z.string().trim().min(1).max(120),
        bytes: z.number().int().nonnegative(),
      })
    ),
  }),
});

export type AdminDashboardUsage = z.infer<typeof adminDashboardUsageSchema>;

export const userMenuUsageSummarySchema = z.object({
  userId: z.string().trim().min(1).max(120),
  tokenRange: z.literal('all_time'),
  totalTokens: z.number().int().nonnegative(),
  totalBytes: z.number().int().nonnegative(),
  updatedAt: z.coerce.date(),
});

export type UserMenuUsageSummary = z.infer<typeof userMenuUsageSummarySchema>;

export const platformCredentialScopeSchema = z.enum(['ai', 'ocr', 'ncbi']);
export type PlatformCredentialScope = z.infer<typeof platformCredentialScopeSchema>;

export const platformCredentialViewSchema = z.object({
  id: z.string().trim().min(1).max(120),
  scope: platformCredentialScopeSchema,
  modelName: z.string().trim().min(1).max(200).nullable(),
  provider: z.string().nullable(),
  baseUrl: z.string().nullable(),
  secretMasked: z.string().trim().min(1).max(500),
  secretCount: z.number().int().nonnegative(),
  secretEncoding: z.enum(['plain', 'base64']),
  isActive: z.boolean(),
  version: z.number().int().positive(),
  updatedByUserId: z.string().trim().min(1).max(120),
  updatedAt: z.coerce.date(),
});

export type PlatformCredentialView = z.infer<typeof platformCredentialViewSchema>;

export const updatePlatformCredentialSchema = z.object({
  modelName: z.string().trim().min(1).max(200),
  provider: z.string().trim().max(100).nullable().optional(),
  baseUrl: z.string().trim().max(500).nullable().optional(),
  secretText: z.string().trim().min(1).max(4000),
  secretEncoding: z.enum(['plain', 'base64']).default('plain'),
  isActive: z.boolean().default(true),
});

export type UpdatePlatformCredentialRequest = z.infer<typeof updatePlatformCredentialSchema>;

export const aiRuntimeSettingsSchema = z.object({
  defaultModel: z.string().trim().min(1).max(200),
  perKeyConcurrency: z.number().int().min(1).max(6),
  ocrDefaultModel: z.string().trim().min(1).max(200),
  ocrPerKeyConcurrency: z.number().int().min(1).max(6),
  tokenPriceCnyPerMillion: z.number().min(0).max(1000000),
  updatedByUserId: z.string().trim().min(1).max(120).nullable().optional(),
  updatedAt: z.coerce.date(),
});

export type AiRuntimeSettings = z.infer<typeof aiRuntimeSettingsSchema>;

export const updateAiRuntimeSettingsSchema = z
  .object({
    defaultModel: z.string().trim().min(1).max(200).optional(),
    perKeyConcurrency: z.number().int().min(1).max(6).optional(),
    ocrDefaultModel: z.string().trim().min(1).max(200).optional(),
    ocrPerKeyConcurrency: z.number().int().min(1).max(6).optional(),
    tokenPriceCnyPerMillion: z.number().min(0).max(1000000).optional(),
  })
  .superRefine((value, ctx) => {
    const hasAiDefault = typeof value.defaultModel === 'string';
    const hasAiConcurrency = typeof value.perKeyConcurrency === 'number';
    const hasOcrDefault = typeof value.ocrDefaultModel === 'string';
    const hasOcrConcurrency = typeof value.ocrPerKeyConcurrency === 'number';
    const hasTokenPrice = typeof value.tokenPriceCnyPerMillion === 'number';

    if (!hasAiDefault && !hasAiConcurrency && !hasOcrDefault && !hasOcrConcurrency && !hasTokenPrice) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one runtime settings field is required',
        path: ['defaultModel'],
      });
      return;
    }

    if (hasAiDefault !== hasAiConcurrency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'defaultModel and perKeyConcurrency must be provided together',
        path: hasAiDefault ? ['perKeyConcurrency'] : ['defaultModel'],
      });
    }

    if (hasOcrDefault !== hasOcrConcurrency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ocrDefaultModel and ocrPerKeyConcurrency must be provided together',
        path: hasOcrDefault ? ['ocrPerKeyConcurrency'] : ['ocrDefaultModel'],
      });
    }
  });

export type UpdateAiRuntimeSettingsRequest = z.infer<typeof updateAiRuntimeSettingsSchema>;

export const aiRuntimePricingSchema = z.object({
  tokenPriceCnyPerMillion: z.number().min(0).max(1000000),
});

export type AiRuntimePricing = z.infer<typeof aiRuntimePricingSchema>;

export const ncbiPlatformConfigSchema = z.object({
  email: z.string().trim().email().nullable(),
  toolName: z.string().trim().max(200).nullable(),
  retmax: z.number().int().min(1).max(3000).nullable(),
  keyConfigured: z.boolean(),
  secretEncoding: z.enum(['plain', 'base64']),
  updatedAt: z.coerce.date().nullable(),
});

export type NcbiPlatformConfig = z.infer<typeof ncbiPlatformConfigSchema>;

export const updateNcbiPlatformConfigSchema = z.object({
  email: z.string().trim().email().nullable().optional(),
  toolName: z.string().trim().max(200).nullable().optional(),
  retmax: z.number().int().min(1).max(3000).nullable().optional(),
  secretText: z.string().trim().min(1).max(4000),
  secretEncoding: z.enum(['plain', 'base64']).default('plain'),
});

export type UpdateNcbiPlatformConfigRequest = z.infer<typeof updateNcbiPlatformConfigSchema>;

export const adminDashboardStorageSchema = z.object({
  metric: z.literal('d1_db_size'),
  bytes: z.number().int().nonnegative(),
  source: z.enum(['cloudflare_api', 'cache']),
  stale: z.boolean(),
  cachedUntil: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type AdminDashboardStorage = z.infer<typeof adminDashboardStorageSchema>;

// ============= Error Response Schema =============

export const errorResponseSchema = z.object({
  error: z.string(),
  code: z.string(),
  details: z.unknown().optional(),
});

export type ErrorResponse = z.infer<typeof errorResponseSchema>;
